import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface PageSection {
  id: string;
  section_key: string;
  section_type?: string;
  title?: string | null;
  subtitle?: string | null;
  content?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  button_label?: string | null;
  button_url?: string | null;
  is_visible?: boolean;
  extra_data?: any;
}

/**
 * Lit une section éditable depuis page_sections (clé unique).
 * Retourne null si absente/erreur => le composant garde son contenu par défaut (fallback).
 */
export function useSection(sectionKey: string) {
  const [section, setSection] = useState<PageSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    supabase
      .from('page_sections')
      .select('*')
      .eq('section_key', sectionKey)
      .limit(1)
      .then(({ data }) => {
        if (active) { setSection(data && data.length ? (data[0] as PageSection) : null); setLoading(false); }
      })
      .then(undefined, () => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sectionKey]);

  return { section, loading };
}

/**
 * Lit toutes les sections d'une page (par slug) en une requête.
 * Retourne une map { section_key: PageSection }. Vide si page absente/erreur => fallback composant.
 */
export function usePageSections(slug: string) {
  const [sections, setSections] = useState<Record<string, PageSection>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: page } = await supabase.from('pages').select('id').eq('slug', slug).neq('status', 'draft').maybeSingle();
      if (!page) { if (active) setLoading(false); return; }
      const { data } = await supabase.from('page_sections').select('*').eq('page_id', (page as any).id);
      if (!active) return;
      const map: Record<string, PageSection> = {};
      (data || []).forEach((s: any) => { map[s.section_key] = s as PageSection; });
      setSections(map);
      setLoading(false);
    })().then(undefined, () => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  return { sections, loading };
}
